import React, { useMemo, useRef, useState } from 'react';

import { EditorState, Modifier, convertToRaw } from 'draft-js';
import { draftToMarkdown } from 'markdown-draft-js';
import { Editor } from 'react-draft-wysiwyg';

import { Box } from '@mui/material';

import { EmployeeUserOption } from '~/hooks/apis/users/useGetEmployeeUsers';

import ChatterMentionPopover from './ChatterMentionPopover';
import { useMentionSession } from './useMention';
import { findActiveMention } from './utils';

interface Props {
  placeholder: string;
  onChange: (markdown: string) => void;
  autoFocus?: boolean;
}

const TOOLBAR = {
  options: ['inline', 'list', 'link', 'emoji', 'history'],
  inline: { options: ['bold', 'italic', 'strikethrough'] },
  list: { options: ['unordered', 'ordered'] },
  link: { options: ['link'] },
};

const HASHTAG_CONFIG = { separator: ' ', trigger: '#' };

const EDITOR_STYLE = { padding: '0 12px', minHeight: 96 };

function findMentionAtCaret(editorState: EditorState) {
  const selection = editorState.getSelection();
  if (!selection.getHasFocus() || !selection.isCollapsed()) {
    return null;
  }
  const blockKey = selection.getStartKey();
  const offset = selection.getStartOffset();
  const found = findActiveMention(editorState.getCurrentContent().getBlockForKey(blockKey).getText(), offset);
  return found && { ...found, blockKey, offset };
}

// Anchors the suggestion list under the caret, falling back to the editor box when the caret has no size.
function getCaretRect(fallbackElement: HTMLElement | null) {
  const selection = window.getSelection();
  if (selection && selection.rangeCount > 0) {
    const rect = selection.getRangeAt(0).getBoundingClientRect();
    if (rect.height > 0) {
      return rect;
    }
  }
  return fallbackElement?.getBoundingClientRect() ?? new DOMRect();
}

// Uncontrolled: remount with a new `key` to clear it. Emits markdown, '' when empty.
export default function ChatterEditor({ placeholder, onChange, autoFocus }: Props) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [editorState, setEditorState] = useState(() => EditorState.createEmpty());

  const found = findMentionAtCaret(editorState);
  const caretAnchor = useMemo(() => ({ getBoundingClientRect: () => getCaretRect(wrapperRef.current) }), []);

  const handleChange = (nextState: EditorState) => {
    if (nextState.getCurrentContent() !== editorState.getCurrentContent()) {
      mention.reopen();
    }
    setEditorState(nextState);
    const content = nextState.getCurrentContent();
    onChange(content.hasText() ? draftToMarkdown(convertToRaw(content)).trim() : '');
  };

  const insertMention = (employee: EmployeeUserOption) => {
    if (!found) {
      return;
    }
    const content = editorState.getCurrentContent();
    const mentionRange = editorState.getSelection().merge({
      anchorKey: found.blockKey,
      anchorOffset: found.start,
      focusKey: found.blockKey,
      focusOffset: found.offset,
    });
    const nextContent = Modifier.replaceText(content, mentionRange, `@${employee.full_name} `);
    const pushed = EditorState.push(editorState, nextContent, 'insert-characters');
    handleChange(EditorState.forceSelection(pushed, nextContent.getSelectionAfter()));
  };

  const mention = useMentionSession(
    found && { id: `${found.blockKey}:${found.start}`, query: found.query },
    insertMention,
  );

  return (
    <Box
      ref={wrapperRef}
      // Capture phase, so the list's keys are consumed before the editor inserts a newline or moves the caret.
      onKeyDownCapture={(event) => {
        if (mention.handleKey(event)) {
          event.stopPropagation();
        }
      }}
      sx={{ border: 1, borderColor: 'divider', borderRadius: 1, bgcolor: 'background.paper' }}
    >
      <Editor
        editorState={editorState}
        onEditorStateChange={handleChange}
        toolbar={TOOLBAR}
        hashtag={HASHTAG_CONFIG}
        placeholder={placeholder}
        editorStyle={EDITOR_STYLE}
        ariaLabel={placeholder}
        editorRef={(editor) => (autoFocus ? editor?.focus() : undefined)}
      />
      <ChatterMentionPopover
        isOpen={mention.isOpen}
        anchorEl={caretAnchor}
        suggestions={mention.suggestions}
        highlightedIndex={mention.highlightedIndex}
        isError={mention.isError}
        onSelect={mention.selectEmployee}
      />
    </Box>
  );
}
